import { Translation } from '@ps/web-shared-components';
import type { EventTypeId } from '@ps/web-sports-enums';
import { useSelector } from 'react-redux';

import type {
  SearchMatch,
  SearchMatches,
  SearchResults
} from 'widgets/sports-menu-widget/model/types';

import { useSportsMenuWidget } from '../../../../model';
import { LoadingState } from '../LoadingState/LoadingState';
import { SearchResultsDropdown } from '../SearchResultsDropdown/SearchResultsDropdown';

import { CompetitionsResultGroup } from './ResultGroup/CompetitionsResultGroup/CompetitionsResultGroup';
import { EventsResultGroup } from './ResultGroup/EventsResultGroup/EventsResultGroup';
import { MeetingsResultGroup } from './ResultGroup/MeetingsResultGroup/MeetingsResultGroup';
import { RunnersResultGroup } from './ResultGroup/RunnersResultGroup/RunnersResultGroup';
import { TXTMOB_SorryX_no_results_were_foundX } from './SearchResults.messages.json';
import styles from './SearchResults.scss';
import SportsPills from './SportsPills/SportsPills';

function isEventTypeIdMatching(match: SearchMatch, eventTypeId: number) {
  return match.link.eventTypeId === eventTypeId;
}

function getMatchesByEventTypeId(
  matches: SearchResults['matches'],
  eventTypeId: number
): SearchMatches {
  return {
    events: matches.events.filter((match) => isEventTypeIdMatching(match, eventTypeId)),
    competitions: matches.competitions.filter((match) => isEventTypeIdMatching(match, eventTypeId)),
    runners: matches.runners.filter((match) => isEventTypeIdMatching(match, eventTypeId)),
    meetings: matches.meetings.filter((match) => isEventTypeIdMatching(match, eventTypeId))
  };
}

function getIsPrevSelectedInNewResults(
  uniqueEventTypes: SearchResults['uniqueEventTypes'],
  previouslySelectedEventTypeId: EventTypeId
) {
  return uniqueEventTypes.some(
    (eventType) => eventType.eventTypeId === previouslySelectedEventTypeId
  );
}

export const SearchResultsContainer: React.FC = () => {
  const { widgetSelect } = useSportsMenuWidget();
  const previouslySelectedEventTypeId = useSelector(widgetSelect.selectedEventTypeId);
  const isFetchingSearch = useSelector(widgetSelect.isFetchingSearch);
  const isNewSearchQuery = useSelector(widgetSelect.isNewSearchQuery);

  const { matches, uniqueEventTypes } = useSelector(widgetSelect.searchResults);

  if (isFetchingSearch || isNewSearchQuery) {
    return (
      <SearchResultsDropdown>
        <div className={styles.noResults}>
          <LoadingState />
        </div>
      </SearchResultsDropdown>
    );
  }

  const hasResults = uniqueEventTypes.length > 0;

  if (!hasResults) {
    return (
      <SearchResultsDropdown>
        <div className={styles.noResults}>
          <Translation {...TXTMOB_SorryX_no_results_were_foundX} />
        </div>
      </SearchResultsDropdown>
    );
  }

  const [firstEventType] = uniqueEventTypes;

  const isPrevSelectedInNewResults =
    previouslySelectedEventTypeId &&
    getIsPrevSelectedInNewResults(uniqueEventTypes, previouslySelectedEventTypeId);

  const selectedEventTypeId = isPrevSelectedInNewResults
    ? previouslySelectedEventTypeId
    : firstEventType.eventTypeId;

  const { events, competitions, runners, meetings } = getMatchesByEventTypeId(
    matches,
    selectedEventTypeId
  );

  return (
    <SearchResultsDropdown>
      <SportsPills selectedEventTypeId={selectedEventTypeId} />
      <EventsResultGroup key={'events' + selectedEventTypeId} events={events} />
      <CompetitionsResultGroup
        key={'competitions' + selectedEventTypeId}
        competitions={competitions}
      />
      <RunnersResultGroup key={'runners' + selectedEventTypeId} runners={runners} />
      <MeetingsResultGroup key={'meetings' + selectedEventTypeId} meetings={meetings} />
    </SearchResultsDropdown>
  );
};
